import {
  CylinderGeometry,
  MeshBasicMaterial,
  Mesh,
  Vector3,
  TorusGeometry,
  Object3D,
  Group,
} from "three";
import { Context } from "../context";
import { PoserClock } from "../util/clock";
import { Ball } from "./ball/ball";
import { DefensivePlayMode, DefensivePlayState } from "../managers/playstate";

const PITCH_TOLERANCE = 2000;
const HALF_PI = 0.5 * Math.PI;
const plateWidth = 17 / 12;
const thickness = 0.1 / 12;
const halfPlateWidth = 0.5 * plateWidth;
const width = plateWidth; // + 2 * extension;
const material = new MeshBasicMaterial({ color: "#000" });
const n = new Vector3(0, 0, -1); // normal of the strike plane
const p0 = new Vector3(0, 0, -plateWidth); //point in the strike plane
const radius = 0.2;

class Spot extends Object3D {
  private static disk = new CylinderGeometry(radius, radius, 3 * thickness, 16);
  private static ring = new TorusGeometry(radius + 0.1, 3 * thickness, 2, 24);
  private material = new MeshBasicMaterial({
    color: "#fff",
    opacity: 1.0,
    transparent: true,
  });
  shouldRetire: boolean = false;
  dead: boolean = false;

  constructor(pos?: Vector3) {
    super();
    this.add(new Mesh(Spot.disk, this.material));
    this.add(new Mesh(Spot.ring, this.material));
    this.children[0].rotation.x = HALF_PI;

    if (pos) this.position.copy(pos);
  }

  fadeAndScale(fade: number, scale: number, reset?: boolean) {
    if (reset) {
      this.material.opacity = 1.0;
      this.scale.set(1.0, 1.0, 1.0);
    } else {
      this.material.opacity *= fade;
      this.scale.multiplyScalar(scale);
    }
  }

  age() {
    if (this.material.opacity > 0.5 || this.shouldRetire) {
      this.fadeAndScale(0.995, 0.995, false);
    }
    if (this.material.opacity < 0.01) {
      this.dead = true;
    }
  }
}

export class Strikezone extends Object3D {
  contextRef: Context;

  private horizontal: CylinderGeometry;
  private vertical: CylinderGeometry;
  private top: Mesh;
  private bottom: Mesh;
  private left: Mesh;
  private right: Mesh;
  private spot: Spot;
  prevIndex: number;
  lastPitchTime: number;
  currBall: Vector3;
  prevBall: Vector3;
  dir: Vector3;
  canMakeSpot: boolean;

  constructor(context: Context, upper: number = 3.5, lower: number = 1) {
    super();

    this.contextRef = context;
    this.prevIndex = -1;
    this.lastPitchTime = 0;
    this.canMakeSpot = false;
    this.currBall = new Vector3(0, 0, 0);
    this.prevBall = new Vector3(0, 0, 0);
    this.dir = new Vector3(0, 0, 0);
    let height = upper - lower;
    let yMid = 0.5 * (lower + upper);

    this.horizontal = new CylinderGeometry(thickness, thickness, width, 8);
    this.vertical = new CylinderGeometry(thickness, thickness, height, 8);

    this.top = new Mesh(this.horizontal, material);
    this.bottom = new Mesh(this.horizontal, material);
    this.left = new Mesh(this.vertical, material);
    this.right = new Mesh(this.vertical, material);

    this.top.position.set(0, upper, -plateWidth);
    this.top.rotation.z = HALF_PI;
    this.bottom.position.set(0, lower, -plateWidth);
    this.bottom.rotation.z = HALF_PI;
    this.left.position.set(-halfPlateWidth, yMid, -plateWidth);
    this.right.position.set(halfPlateWidth, yMid, -plateWidth);

    this.spot = new Spot(new Vector3(0, -5, 0));
    this.add(this.top);
    this.add(this.bottom);
    this.add(this.left);
    this.add(this.right);
    this.add(this.spot);

    this.contextRef.playStateManager?.addSubscriber(
      "strikezone",
      (defense: DefensivePlayState) => {
        if (defense.mode == DefensivePlayMode.PITCHING) {
          // this event can fire multiple times or during non-pitches if
          // OffensivePlayState or GameMode updates during the at bat
          // Such as an END_OF_PLAY event or switching from WARMUP to LIVE
          this.canMakeSpot = true;
          this.lastPitchTime = context.clock.realMs;
        } else {
          this.canMakeSpot = false;
        }
      }
    );
  }

  update(clock: PoserClock) {
    this.updateStrikebox();

    let delta = clock.realMs - this.lastPitchTime;
    if (this.canMakeSpot && delta > 0 && delta < PITCH_TOLERANCE) {
      this.updateSpot(this.contextRef.world?.ball);
    }

    if (this.contextRef.state.playing) {
      this.spot.fadeAndScale(0.995, 0.995);
    }
  }

  updateStrikebox() {
    let index = this.contextRef.gameState.getPlayIndex();

    // If the ID of the current batter has changed, adjust the strike box to his size
    if (index && index != this.prevIndex) {
      let latest = this.contextRef.gameState.getStrikeZone();
      if (!latest || !latest.top || !latest.bottom) return;
      let upper = latest.top;
      let lower = latest.bottom;
      let h = upper - lower;

      this.vertical = new CylinderGeometry(thickness, thickness, h, 8);
      this.left.geometry = this.vertical;
      this.right.geometry = this.vertical;

      let yMid = 0.5 * (upper + lower);
      this.top.position.set(0, upper, -plateWidth);
      this.bottom.position.set(0, lower, -plateWidth);
      this.left.position.set(-halfPlateWidth, yMid, -plateWidth);
      this.right.position.set(halfPlateWidth, yMid, -plateWidth);

      this.prevIndex = index;
    }
  }

  updateSpot(ball?: Ball) {
    if (!ball?.ball || !ball.ball.position) return;

    this.currBall.copy(ball.ball.position);
    /**
     * Check if the z-position of the ball in the frame data is past the strike plane.
     * The plane is extended forward by three plate widths to account for inconsistencies in frame data.
     * The positive z-axis points away from the pitcher, so -plateWidth*3 extends three plateWidths away
     * from the tip of home plate in the direction of the pitcher.
     */
    if (this.currBall.z >= -plateWidth * 3) {
      this.dir.subVectors(this.currBall, this.prevBall);
      // is the direction of travel pointing towards the catcher or away
      if (this.dir.z > 0) {
        // using the direction from prev to curr, find the point on the strike plane it intersects
        let d = p0.clone().sub(this.prevBall).dot(n) / this.dir.dot(n);
        let pt = this.prevBall.addScaledVector(this.dir, d);
        this.spot.position.set(pt.x, pt.y, -plateWidth);
        // set prevball far behind home plate to ensure the throw back from the catcher is not counted
        // this will only change back when the ball is past the plane going the other direction
        this.prevBall.set(0, 0, 100);

        // Reset opacity and scale for new pitch
        this.spot.fadeAndScale(-1, -1, true);

        this.canMakeSpot = false;
      }
    } else {
      this.prevBall.copy(this.currBall);
    }
  }
}

/**
 * TODO: Are we in a spot to combine Strikezone and
 * StrikezonePersistent now?
 * https://github.mlbam.net/bdata/poser/pull/523#discussion_r477609
 */
export class StrikezonePersistent extends Strikezone {
  private spots: Group = new Group();

  constructor(context: Context) {
    super(context);
    this.contextRef = context;
    this.add(this.spots);
  }

  update(clock: PoserClock) {
    this.updateStrikebox();

    let delta = clock.realMs - this.lastPitchTime;
    if (this.canMakeSpot && delta > 0 && delta < PITCH_TOLERANCE) {
      this.updateSpot(this.contextRef.world?.ball);
    }

    let count = this.contextRef.gameState.getCount();
    let shouldRetire =
      (count?.balls == 0 && count?.strikes == 0) ||
      count?.balls == 4 ||
      count?.strikes == 3 ||
      count?.outs == 3;
    if (this.contextRef.state.playing) {
      this.spots.children.forEach((spot) => {
        if (spot instanceof Spot) {
          spot.shouldRetire ||= shouldRetire;
          spot.age();
        }
      });
      this.spots.children = this.spots.children.filter((spot) => {
        if (spot instanceof Spot) {
          return !spot.dead;
        }
      });
    }
  }

  updateSpot(ball?: Ball) {
    if (!ball?.ball || !ball.ball.position) return;

    this.currBall.copy(ball.ball.position);
    if (this.currBall.z >= -plateWidth * 3) {
      this.dir.subVectors(this.currBall, this.prevBall);
      if (this.dir.z > 0) {
        let d = p0.clone().sub(this.prevBall).dot(n) / this.dir.dot(n);
        let pt = this.prevBall.addScaledVector(this.dir, d);
        this.spots.add(new Spot(new Vector3(pt.x, pt.y, -plateWidth)));
        this.prevBall.set(0, 0, 100);

        this.canMakeSpot = false;
      }
    } else {
      this.prevBall.copy(this.currBall);
    }
  }

  reset() {
    this.spots.children = [];
  }
}
